/**
 * Optimizes Cloudinary image URLs for better performance
 * @param url - Original Cloudinary URL
 * @param width - Target width in pixels (default: 600)
 * @param quality - Image quality 1-100 (default: 80)
 * @param format - Image format: 'auto', 'webp', 'jpg', 'png' (default: 'auto')
 * @returns Optimized Cloudinary URL
 */
export function optimizeCloudinaryImage(
  url: string,
  width: number = 600,
  quality: number = 80,
  format: 'auto' | 'webp' | 'jpg' | 'png' = 'auto'
): string {
  if (!url || !url.includes('cloudinary.com')) {
    return url;
  }

  // Check if URL already has transformation parameters
  const uploadIndex = url.indexOf('/upload/');
  if (uploadIndex === -1) return url;

  const beforeUpload = url.substring(0, uploadIndex + 8); // '/upload/'
  const afterUpload = url.substring(uploadIndex + 8);

  // Build transformation string
  const transformations = `w_${width},q_${quality},f_${format}`;

  return `${beforeUpload}${transformations}/${afterUpload}`;
}

/**
 * Generates responsive image srcSet for Cloudinary images
 * @param url - Original Cloudinary URL
 * @param widths - Array of widths for srcSet
 * @returns srcSet string
 */
export function generateSrcSet(
  url: string,
  widths: number[] = [300, 600, 900, 1200]
): string {
  if (!url || !url.includes('cloudinary.com')) {
    return '';
  }

  return widths
    .map((width) => {
      const optimizedUrl = optimizeCloudinaryImage(url, width, 80, 'auto');
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
}

